import { useId, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  IconButton,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

/** Examples of the post-text search syntax, kept separate from topic and people search. */
const SEARCH_EXAMPLES = [
  { query: 'wood chop', description: 'Both terms, including word prefixes' },
  { query: 'chop OR spoon', description: 'Either term' },
  { query: '"wooden chopsticks"', description: 'A phrase' },
  { query: 'chop -wooden', description: 'Matches without that word' },
  { query: 'chop -"wooden chopsticks"', description: 'Matches without that phrase' },
  { query: '"chop"', description: 'Whole-word matches instead of prefixes' },
] as const;

/** Explains post-search controls without changing the query or leaving the search dialog. */
export function SearchTips(): React.JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const popoverId = useId();
  const titleId = `${popoverId}-title`;
  const descriptionId = `${popoverId}-description`;
  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        size="small"
        aria-label="Search tips"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          p: 0,
          fontSize: 'inherit',
          fontWeight: 400,
          lineHeight: 'inherit',
          verticalAlign: 'baseline',
          textTransform: 'none',
          whiteSpace: 'nowrap',
          color: 'var(--mui-palette-text-secondary)',
        }}
      >
        Search like it’s 1996 <InfoOutlinedIcon sx={{ fontSize: '1em', ml: 0.5 }} />
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            id: popoverId,
            role: 'dialog',
            'aria-modal': true,
            'aria-labelledby': titleId,
            'aria-describedby': descriptionId,
            sx: { width: 440, maxWidth: 'calc(100vw - 32px)', p: 2 },
          },
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
        >
          <Typography id={titleId} variant="h6" component="h2">
            Search tips
          </Typography>
          <IconButton size="small" aria-label="Close search tips" onClick={() => setAnchorEl(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography id={descriptionId} variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Use these controls to search posts and replies.
        </Typography>
        <Table size="small" aria-label="Search examples" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ pl: 0 }}>Try</TableCell>
              <TableCell sx={{ pr: 0 }}>To find</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SEARCH_EXAMPLES.map(({ query, description }) => (
              <TableRow key={query}>
                <TableCell
                  sx={{
                    pl: 0,
                    pr: 1,
                    width: '48%',
                    verticalAlign: 'top',
                    overflowWrap: 'anywhere',
                  }}
                >
                  <code>{query}</code>
                </TableCell>
                <TableCell sx={{ pr: 0, verticalAlign: 'top' }}>{description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Case doesn’t matter. English word forms still match, even in quotes. AND is implicit and
          takes precedence over OR; parentheses aren’t supported.
        </Typography>
      </Popover>
    </>
  );
}
